import React from 'react';
import { HashRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

// H5 落地页
import ArticlePage       from './pages/Article';
import BloggerChat    from './pages/BloggerChat';
import MerchantAgent  from './pages/MerchantAgent';
import Health         from './pages/Health';
import ContentPage      from './pages/ContentPage';
import LessonArticlePage from './pages/LessonArticle';
import UserProtocol      from './pages/UserProtocol';
import PrivacyPolicy     from './pages/PrivacyPolicy';
import XhsPublishTest    from './pages/XhsPublishTest';
import Exhausted         from './pages/Exhausted';
import SubmitPage        from './pages/Submit';
import EnrollBlogger     from './pages/EnrollBlogger';
import BloggerTasks      from './pages/BloggerTasks';
import MerchantShare     from './pages/MerchantShare';

// 管理后台
import Login         from './pages/admin/Login';
import MerchantList  from './pages/admin/MerchantList';
import CampaignList  from './pages/admin/CampaignList';
import ArticleList   from './pages/admin/ArticleList';
import ArticleDetail from './pages/admin/ArticleDetail';
import UserManagement from './pages/admin/UserManagement';
import Dashboard      from './pages/admin/Dashboard';
import Submissions    from './pages/admin/Submissions';
import BloggerManage  from './pages/admin/BloggerManage';
import QbbAccounts    from './pages/admin/QbbAccounts';
import AgentConsole   from './pages/admin/AgentConsole';
import EvalReview     from './pages/admin/EvalReview';
import TandianActivityDetail from './pages/admin/TandianActivityDetail';
import { adoptTokenFromUrl } from './services/api';
import BloggerWebRetired from './pages/BloggerWebRetired';

// 后台登录入口（隐蔽路径，避免主域名直接暴露后台）
export const ADMIN_LOGIN_PATH = '/ops-9k27x';

// 中性空白页：主域名/未知路径落到这里，不暴露任何后台线索
function Blank() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: 14, background: '#fff' }}>
      页面不存在
    </div>
  );
}

function RequireAuth({ children }) {
  const token = localStorage.getItem('admin_token');
  if (!token) return <Navigate to={ADMIN_LOGIN_PATH} replace />;
  return children;
}

// App 内嵌 webview 会带 ?t=<博主token> 进来，先认领再渲染路由
adoptTokenFromUrl();

function App() {
  return (
    <Router>
      <Routes>
        {/* 主域名 / 未知路径 / 老的 admin 登录路径 → 中性空白，不跳后台 */}
        <Route path="/"            element={<Blank />} />
        <Route path="/admin/login" element={<Blank />} />
        <Route path="*"            element={<Blank />} />

        {/* 隐蔽后台登录入口 */}
        <Route path={ADMIN_LOGIN_PATH} element={<Login />} />

        {/* H5 落地页 */}
        <Route path="/article/:id"        element={<ArticlePage />} />
        <Route path="/lessonArticle/:id"  element={<LessonArticlePage />} />
        <Route path="/userProtocol"       element={<UserProtocol />} />
        <Route path="/privacyPolicy"      element={<PrivacyPolicy />} />
        {/* 老的 Satisfyer 模板页下掉了（2026-09-18）。这个路径可能还贴在
            应用商店条目或别人的书签里，让它落到隐私政策，别死在空白页上 */}
        <Route path="/protectionNotice"   element={<Navigate to="/privacyPolicy" replace />} />
        <Route path="/xhs-publish-test"   element={<XhsPublishTest />} />
        <Route path="/exhausted"           element={<Exhausted />} />
        <Route path="/view/:id"            element={<ContentPage />} />
        <Route path="/submit-store/:merchantId" element={<SubmitPage />} />
        <Route path="/submit-universal"    element={<SubmitPage />} />
        {/* 体检页。路径本身是随机的 —— 不是安全措施（真防护是登录+口令），
            只是让它不出现在任何人的猜测范围里。改路径记得同步书签。 */}
        <Route path="/x/4xezpk6zminb"          element={<Health />} />
        {/* 博主端网页停用（2026-09-19）：博主只走券宝宝 App 里的探店模块。
            不直接删路由 —— 这些链接发过给博主，落到空白页他会以为坏了然后去问运营。
            页面组件（BloggerTasks / EnrollBlogger / BloggerChat）先留着不删，
            要恢复把这四行换回去就行。 */}
        <Route path="/blogger"             element={<BloggerWebRetired />} />
        <Route path="/blogger/enroll"      element={<BloggerWebRetired />} />
        <Route path="/blogger/tasks"       element={<BloggerWebRetired />} />
        <Route path="/blogger/chat"        element={<BloggerWebRetired />} />
        <Route path="/ma/:token"           element={<MerchantAgent />} />
        <Route path="/t/:token"            element={<MerchantShare />} />

        {/* 管理后台（需登录，无 token 自动跳隐蔽登录页） */}
        <Route path="/admin"                                  element={<RequireAuth><MerchantList /></RequireAuth>} />
        <Route path="/admin/users"                            element={<RequireAuth><UserManagement /></RequireAuth>} />
        <Route path="/admin/dashboard"                        element={<RequireAuth><Dashboard /></RequireAuth>} />
        <Route path="/admin/blogger-posts"                    element={<RequireAuth><Submissions /></RequireAuth>} />
        <Route path="/admin/bloggers"                         element={<RequireAuth><BloggerManage /></RequireAuth>} />
        <Route path="/admin/qbb-accounts"                     element={<RequireAuth><QbbAccounts /></RequireAuth>} />
        <Route path="/admin/agent"                            element={<RequireAuth><AgentConsole /></RequireAuth>} />
        {/* 审题面：单独发给指定的人，用口令进，**不需要后台账号**。
            所以这条不套 RequireAuth，鉴权在后端按 X-Eval-Key 做 */}
        <Route path="/e/efdlbxkr9muo"                         element={<EvalReview />} />
        <Route path="/admin/tandian/activity/:activityId"     element={<RequireAuth><TandianActivityDetail /></RequireAuth>} />
        <Route path="/admin/merchant/:merchantId"             element={<RequireAuth><CampaignList /></RequireAuth>} />
        <Route path="/admin/campaign/:campaignId"             element={<RequireAuth><ArticleList /></RequireAuth>} />
        <Route path="/admin/campaign/:campaignId/article/new" element={<RequireAuth><ArticleDetail /></RequireAuth>} />
        <Route path="/admin/article/:articleId"               element={<RequireAuth><ArticleDetail /></RequireAuth>} />
      </Routes>
    </Router>
  );
}

export default App;
